// Helpers de apresentação do Console de Frota (FOTA v1). Puros → testáveis.
// Mantêm rótulos pt-BR e o mapa de cores dos chips num só lugar (sem deriva).
import type {
  FirmwareResult, FirmwareResultDetail, FirmwareAuditEvent, OfferReason, CredsGateReason, CredsCensusDevice,
  DeviceOfferResumeRefusal, FirmwareDeviceStatus,
} from './types'

/**
 * SemVer estrito X.Y.Z (três grupos numéricos, sem prefixo 'v' e sem
 * pré-release/build). É o mesmo piso do servidor — validar no cliente evita
 * um round-trip para um 400 óbvio. Rejeita '1.2', 'v1', '1.2.3-rc1'.
 */
const SEMVER_RE = /^\d+\.\d+\.\d+$/
export const isValidSemver = (v: string): boolean => SEMVER_RE.test(v.trim())

/**
 * Compara duas versões X.Y.Z campo a campo (espelho do cmpSemver do servidor,
 * que decide o anti-downgrade). Devolve null se qualquer lado não for X.Y.Z —
 * o chamador decide o balde do "não sei", nunca uma exceção na renderização.
 * Igualdade de string NÃO serve aqui: '3.9.0' < '3.10.0'.
 */
export const cmpFwVersions = (a: string | null | undefined, b: string | null | undefined): -1 | 0 | 1 | null => {
  if (a == null || b == null) return null
  const ma = SEMVER_RE.exec(a.trim())
  const mb = SEMVER_RE.exec(b.trim())
  if (!ma || !mb) return null
  const pa = a.trim().split('.').map(Number)
  const pb = b.trim().split('.').map(Number)
  for (let i = 0; i < 3; i++) {
    if (pa[i] !== pb[i]) return pa[i] < pb[i] ? -1 : 1
  }
  return 0
}

/** Formata bytes em unidade legível (o slot OTA / tamanho do binário). */
export const fmtBytes = (n: number | null | undefined): string => {
  if (n == null) return '—'
  if (n < 1024) return `${n} B`
  const kb = n / 1024
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`
  const mb = kb / 1024
  return `${mb.toFixed(mb < 10 ? 2 : 1)} MB`
}

/** SHA-256 curto p/ a tabela (primeiros 12 hex + reticências). */
export const shortSha = (sha: string | null | undefined): string =>
  !sha ? '—' : sha.length <= 14 ? sha : `${sha.slice(0, 12)}…`

// ── Resultado da última tentativa (chip colorido por semântica) ──────────────
export interface FirmwareResultMeta {
  label: string
  color: 'success' | 'info' | 'error' | 'default'
}

const RESULT_META: Record<FirmwareResult, FirmwareResultMeta> = {
  health_confirmed: { label: 'Saúde confirmada', color: 'success' },
  installed: { label: 'Instalado', color: 'info' },
  failed: { label: 'Falhou', color: 'error' },
  rolled_back: { label: 'Revertido', color: 'error' },
}

/** null → chip neutro "—" (device nunca reportou resultado). */
export const firmwareResultMeta = (r: FirmwareResult | null | undefined): FirmwareResultMeta =>
  r == null ? { label: '—', color: 'default' } : RESULT_META[r] ?? { label: r, color: 'default' }

/** Detalhe do porquê da falha/rollback — vira o tooltip do chip. */
export const RESULT_DETAIL_LABEL: Record<FirmwareResultDetail, string> = {
  sha256_mismatch: 'Integridade SHA-256 não conferiu',
  no_space: 'Sem espaço no slot OTA',
  crash_before_healthy: 'Reiniciou antes de confirmar saúde',
  heap_low: 'Memória (heap) insuficiente',
  download_failed: 'Falha no download do binário',
  url_rejected: 'URL do binário rejeitada pelo device',
  unhealthy_timeout: 'Não confirmou saúde no prazo',
}

export const resultDetailLabel = (d: FirmwareResultDetail | null | undefined): string | null =>
  d == null ? null : RESULT_DETAIL_LABEL[d] ?? d

// ── Causa fina da desistência do download (fw ≥ 3.52.0, raiz_web#124) ───────
// O `detail` segue enum fechado (download_failed) porque um valor novo ali faria
// o servidor antigo descartar o fw_result inteiro; a causa fina viaja em
// `detail_ext`, TEXTO LIVRE ≤ 45 chars:
//   <decisão>[/<causa>] b=<gravados>/<total> r=<religadas> t=<segundos>s
// Texto livre = o parse nunca lança e nunca inventa: o que não casar fica null,
// e o rótulo cai no texto cru quando a decisão não é do vocabulário conhecido
// (`timeout` é reservado e não sai na 3.52.0 — se sair, aparece cru).

export interface DetailExt {
  /** budget | stall | conn_lost | heap | no_range | http_<código> — cru, como veio. */
  decision: string | null
  /** Só com decisão budget e evento negativo antes (a "barra"). */
  cause: string | null
  /** Bytes gravados — ORDEM DE GRANDEZA: pode sair um chunk atrás do flash. */
  bytes: number | null
  total: number | null
  reconnects: number | null
  seconds: number | null
}

const DETAIL_EXT_HEAD_RE = /^([a-z][a-z0-9_]*)(?:\/([a-z][a-z0-9_]*))?$/i
const safeInt = (s: string | undefined): number | null => {
  if (s == null) return null
  const n = Number(s)
  return Number.isSafeInteger(n) ? n : null
}

/**
 * Quebra o `detail_ext` em campos. null para não-texto, vazio ou nada
 * reconhecível; campo que não casar fica null (o resto segue valendo).
 */
export const parseDetailExt = (ext: unknown): DetailExt | null => {
  if (typeof ext !== 'string') return null
  const tokens = ext.trim().split(/\s+/).filter((t) => t !== '')
  if (tokens.length === 0) return null
  const out: DetailExt = { decision: null, cause: null, bytes: null, total: null, reconnects: null, seconds: null }
  const head = DETAIL_EXT_HEAD_RE.exec(tokens[0])
  if (head) {
    out.decision = head[1]
    out.cause = head[2] ?? null
  }
  for (const tok of tokens) {
    // O primeiro de cada campo vale: um token repetido não sobrescreve.
    const b = /^b=(\d+)(?:\/(\d+))?$/.exec(tok)
    if (b) {
      if (out.bytes == null) out.bytes = safeInt(b[1])
      if (out.total == null) out.total = safeInt(b[2])
      continue
    }
    const r = /^r=(\d+)$/.exec(tok)
    if (r) {
      if (out.reconnects == null) out.reconnects = safeInt(r[1])
      continue
    }
    const t = /^t=(\d+)s?$/.exec(tok)
    if (t && out.seconds == null) out.seconds = safeInt(t[1])
  }
  return Object.values(out).some((v) => v != null) ? out : null
}

/** Termos do vocabulário no fio (decisão e causa usam o mesmo). */
const DETAIL_EXT_TERM: Record<string, string> = {
  stall: 'download travou',
  conn_lost: 'queda de conexão',
  heap: 'memória baixa',
  no_range: 'o servidor não retomou (Range)',
}

const detailExtTerm = (tok: string): string | null => {
  const t = tok.toLowerCase()
  if (DETAIL_EXT_TERM[t]) return DETAIL_EXT_TERM[t]
  const http = /^http_(\d+)$/.exec(t)
  return http ? `HTTP ${http[1]}` : null
}

/**
 * O `detail_ext` em pt-BR de tooltip, ex.:
 * "esgotou o prazo ou as religadas (queda de conexão) · parou perto de 793 KB de 1.24 MB · 8 religadas · 412 s".
 *
 * `budget` SEM barra quer dizer que não houve evento negativo nenhum: o prazo
 * de 10 min venceu em link lento — a frase diz isso em vez de "religadas".
 * "perto de" porque o b= é ordem de grandeza; NUNCA vire isto em "% concluído".
 * Decisão desconhecida (ou ausente) → o texto cru: não fingimos explicar.
 */
export const detailExtLabel = (ext: string | null | undefined): string | null => {
  if (typeof ext !== 'string') return null
  const raw = ext.trim()
  if (raw === '') return null
  const p = parseDetailExt(raw)
  if (p == null || p.decision == null) return raw
  let head: string
  if (p.decision.toLowerCase() === 'budget') {
    head = p.cause == null
      ? 'esgotou o prazo sem queda (link lento)'
      : `esgotou o prazo ou as religadas (${detailExtTerm(p.cause) ?? p.cause})`
  } else {
    const term = detailExtTerm(p.decision)
    if (term == null) return raw
    head = p.cause == null ? term : `${term} (${detailExtTerm(p.cause) ?? p.cause})`
  }
  const parts = [head]
  if (p.bytes != null) {
    if (p.bytes === 0) parts.push(p.total != null ? `nada gravado de ${fmtBytes(p.total)}` : 'nada gravado')
    else parts.push(`parou perto de ${fmtBytes(p.bytes)}${p.total != null ? ` de ${fmtBytes(p.total)}` : ''}`)
  }
  if (p.reconnects != null) {
    parts.push(p.reconnects === 0 ? 'sem religadas' : `${p.reconnects} religada${p.reconnects === 1 ? '' : 's'}`)
  }
  if (p.seconds != null) parts.push(`${p.seconds} s`)
  return parts.join(' · ')
}

// ── Eventos da trilha de auditoria (chip por tipo) ───────────────────────────
export interface FirmwareEventMeta {
  label: string
  color: 'success' | 'info' | 'error' | 'primary' | 'warning' | 'default'
}

const EVENT_META: Record<FirmwareAuditEvent, FirmwareEventMeta> = {
  release_uploaded: { label: 'Release publicada', color: 'primary' },
  release_activated: { label: 'Release ativada', color: 'primary' },
  release_deactivated: { label: 'Oferta interrompida', color: 'warning' },
  offered: { label: 'Ofertado', color: 'info' },
  fw_result: { label: 'Resultado', color: 'default' },
  record_annotation: { label: 'Anotação de correção', color: 'warning' },
  factory_uploaded: { label: 'Imagem de fábrica anexada', color: 'primary' },
  bench_flashed: { label: 'Flash de bancada', color: 'info' },
  canary_set: { label: 'Canário definido', color: 'info' },
  canary_cleared: { label: 'Liberada para a frota', color: 'primary' },
  offer_resumed: { label: 'Oferta retomada', color: 'primary' },
}

export const firmwareEventMeta = (e: FirmwareAuditEvent): FirmwareEventMeta =>
  EVENT_META[e] ?? { label: e, color: 'default' }

// ── Retomar oferta por device (issue #122) ──────────────────────────────────
// O servidor reabre a oferta (marco `offer_resumed` na trilha); o freio da
// PRÓPRIA placa — blacklist da versão + cooloff global na NVS do firmware —
// continua, e o beacon não o carrega. O console só pode ESTIMAR pela trilha:
// falhas terminais desde o último health_confirmed (`brake_failures`) e a
// última delas (`brake_last_failure_at`), contra a tabela do firmware.

/**
 * Espelho de `fotaBackoffSeconds` do firmware (raiz_iot lib/fota_core):
 * 1ª falha 30 min, 2ª 2 h, 3ª 8 h, da 4ª em diante 24 h (cap). Vale tanto
 * para a blacklist da versão quanto para o cooloff global.
 */
const LOCAL_BRAKE_BACKOFF_S = [0, 1800, 7200, 28800] as const
const LOCAL_BRAKE_CAP_S = 86400
export const localBrakeBackoffSeconds = (failures: number): number => {
  const n = Math.trunc(Number(failures) || 0)
  if (n <= 0) return 0
  return n < LOCAL_BRAKE_BACKOFF_S.length ? LOCAL_BRAKE_BACKOFF_S[n] : LOCAL_BRAKE_CAP_S
}

/** "30 min" / "2 h" / "8 h" / "24 h" — o prazo do freio pela contagem; null sem falha. */
export const localBrakeLabel = (failures: number): string | null => {
  const s = localBrakeBackoffSeconds(failures)
  if (s === 0) return null
  return s < 3600 ? `${Math.round(s / 60)} min` : `${Math.round(s / 3600)} h`
}

/**
 * Até quando a placa deve segurar o download: última falha + backoff pela
 * contagem. null sem falha, sem data ou data inválida — é ESTIMATIVA (o
 * carimbo da trilha chega até 5 min depois do freio real); a tela diz "~".
 */
export const localBrakeUntil = (failures: number, lastFailureAt: string | null | undefined): Date | null => {
  const s = localBrakeBackoffSeconds(failures)
  if (s === 0 || lastFailureAt == null) return null
  const t = Date.parse(lastFailureAt)
  if (!Number.isFinite(t)) return null
  return new Date(t + s * 1000)
}

/**
 * Retomada gravada e nenhum beacon avaliado depois: o servidor já zerou a
 * contagem (não está mais suspensa), mas `last_offer_reason` ainda é o do
 * beacon anterior — a legenda antiga mentiria. Some sozinho no próximo
 * check-in (o motivo novo fica mais recente que a retomada).
 */
export const offerResumedPending = (d: {
  offer_suppressed: boolean
  offer_resumed_at: string | null
  last_offer_reason_at: string | null
}): boolean => {
  if (d.offer_resumed_at == null || d.offer_suppressed) return false
  if (d.last_offer_reason_at == null) return true
  return Date.parse(d.offer_resumed_at) > Date.parse(d.last_offer_reason_at)
}

// ── Freio local ANUNCIADO pela placa (fw ≥ 3.52.0, raiz_web#124) ────────────
// Da 3.52.0 em diante o envelope carrega o freio (fota_hold_until/_reason/_ver)
// quando há um ativo e a placa tem relógio — o console deixa de estimar pela
// trilha. O RÓTULO muda com o motivo, o efeito prático quase nunca:
//   blacklist → a versão fota_hold_ver fica recusada até lá; MAS na 3.52.0 toda
//               falha terminal com relógio grava a blacklist E um cooloff
//               GLOBAL com a mesma tabela (30 min → 2 h → 8 h → 24 h), e o
//               envelope manda só o dominante — no empate, vence blacklist.
//               Outra versão também deve esperar: nunca prometa "aceita na hora";
//   cooloff / spacing → GLOBAIS: nenhum download, de versão nenhuma, até lá.
// Ponto cego de 1 beacon: uma blacklist gravada sem relógio só entra no envelope
// depois do carimbo da resposta seguinte — ausência NÃO prova ausência de freio.
// Api antiga (campos AUSENTES, === undefined) não repassa o freio mesmo com fw
// novo: aí o console volta a estimar pela trilha — ver holdReported.

/** Primeira versão que anuncia o freio no envelope. */
export const FOTA_HOLD_FROM = '3.52.0'

/** O fw anuncia o freio? X.Y.Z ≥ 3.52.0; null/lixo = não (cai na estimativa). */
export const fwReportsHold = (fw: string | null | undefined): boolean =>
  (cmpFwVersions(fw, FOTA_HOLD_FROM) ?? -1) >= 0

/**
 * O freio anunciado CHEGOU até o console? fw ≥ 3.52.0 E `fota_hold_until`
 * presente no payload (string ou null). Distingue:
 *   ausente (=== undefined) → api antiga, que não repassa o campo: não se sabe
 *     nada do freio — estima pela trilha, como no fw antigo;
 *   null → api nova: a placa não anunciou freio (ausência não é prova — ponto
 *     cego de 1 beacon).
 * É A regra dos dois lugares que falam do freio na retomada (legenda
 * "Retomada" e diálogo): não duplique a condição.
 */
export const holdReported = (d: { fw: string | null | undefined, fota_hold_until?: string | null }): boolean =>
  fwReportsHold(d.fw) && d.fota_hold_until !== undefined

type FotaHoldFields = Pick<FirmwareDeviceStatus, 'fota_hold_until' | 'fota_hold_reason' | 'fota_hold_ver'>

/** O prazo anunciado como Date; null sem freio ou com data inválida. */
export const fotaHoldUntil = (d: FotaHoldFields): Date | null => {
  if (d.fota_hold_until == null) return null
  const t = Date.parse(d.fota_hold_until)
  return Number.isFinite(t) ? new Date(t) : null
}

/** O prazo ainda não venceu (o valor é do último beacon — pode ter vencido depois dele). */
export const fotaHoldActive = (d: FotaHoldFields, now: Date = new Date()): boolean => {
  const until = fotaHoldUntil(d)
  return until != null && until.getTime() > now.getTime()
}

// Fuso do navegador, como o resto do painel; mesmo formato de raizFormat
// (fmtHora/fmtDiaHora são privados lá): "14:32" hoje, "12/09, 14:32" em outro dia.
const fmtHoraCurta = (d: Date): string => d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
const fmtDiaHoraCurta = (d: Date): string =>
  d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
/** "14:32" se `d` é hoje (no fuso do navegador), senão "12/09, 14:32". */
export const fmtHoldInstant = (d: Date, now: Date = new Date()): string =>
  d.toDateString() === now.toDateString() ? fmtHoraCurta(d) : fmtDiaHoraCurta(d)

/**
 * O freio como frase curta de linha — null se não há freio ativo:
 *   blacklist → "pausado para a versão 3.52.0 até 14:32"
 *   cooloff   → "nenhum download até 14:32 (cooloff)"
 *   spacing   → "nenhum download até 14:32 (espaçamento)"
 *   sem motivo → "nenhum download até 14:32" (o servidor anula motivo fora do
 *   vocabulário e o prazo segue valendo: na dúvida, global).
 */
export const fotaHoldText = (d: FotaHoldFields, now: Date = new Date()): string | null => {
  if (!fotaHoldActive(d, now)) return null
  const ate = fmtHoldInstant(fotaHoldUntil(d) as Date, now)
  switch (d.fota_hold_reason) {
    case 'blacklist':
      return d.fota_hold_ver
        ? `pausado para a versão ${d.fota_hold_ver} até ${ate}`
        : `pausado para uma versão até ${ate}`
    case 'cooloff': return `nenhum download até ${ate} (cooloff)`
    case 'spacing': return `nenhum download até ${ate} (espaçamento)`
    default: return `nenhum download até ${ate}`
  }
}

/**
 * O freio anunciado segura a oferta? Com prazo, SEMPRE — qualquer motivo,
 * qualquer versão. Blacklist não é exceção: na 3.52.0 a mesma falha terminal
 * grava blacklist da versão E cooloff global com a mesma tabela, e o envelope
 * só manda o dominante (no empate, blacklist). "Pausado para a versão X" quase
 * sempre esconde um cooloff global de mesmo prazo — outra versão não passa na
 * hora. Não olha se venceu: combine com fotaHoldActive (ou fotaHoldText).
 */
export const fotaHoldBlocksVersion = (d: FotaHoldFields): boolean => fotaHoldUntil(d) != null

/**
 * Blacklist ativa de OUTRA versão que não `versao`: o que isso significa para
 * ela, sem prometer aceitação — "A 3.53.1 é outra versão, mas também deve
 * esperar até 14:32 — o cooloff global da mesma falha costuma vencer junto."
 * null sem freio ativo, fora da blacklist, ou com a versão igual/desconhecida
 * (aí o próprio fotaHoldText já diz a versão pausada).
 */
export const fotaHoldOtherVersionText = (
  d: FotaHoldFields, versao: string | null | undefined, now: Date = new Date(),
): string | null => {
  if (!fotaHoldActive(d, now) || d.fota_hold_reason !== 'blacklist') return null
  if (d.fota_hold_ver == null || versao == null || d.fota_hold_ver.trim() === versao.trim()) return null
  const ate = fmtHoldInstant(fotaHoldUntil(d) as Date, now)
  return `A ${versao.trim()} é outra versão, mas também deve esperar até ${ate} — o cooloff global da mesma falha costuma vencer junto.`
}

/**
 * Tooltip do freio: a frase + o que ela significa para a oferta ATUAL
 * (`active_version`). null sem freio ativo.
 */
export const fotaHoldHint = (
  d: FotaHoldFields & { active_version?: string | null },
  now: Date = new Date(),
): string | null => {
  const text = fotaHoldText(d, now)
  if (text == null) return null
  const base = `A placa informou no último beacon: ${text}.`
  switch (d.fota_hold_reason) {
    case 'blacklist': {
      const outra = fotaHoldOtherVersionText(d, d.active_version, now)
      if (outra != null) return `${base} ${outra}`
      const ate = fmtHoldInstant(fotaHoldUntil(d) as Date, now)
      return `${base} Blacklist depois de falha terminal; outra versão também deve esperar até ${ate} — o cooloff global da mesma falha costuma vencer junto.`
    }
    case 'cooloff':
      return `${base} Cooloff é freio global depois de falha terminal: nenhuma versão é baixada até lá, e retomar a oferta não adianta.`
    case 'spacing':
      return `${base} Espaçamento mínimo entre downloads: nenhuma versão é baixada até lá.`
    default:
      return `${base} Nenhuma versão é baixada até lá.`
  }
}

/**
 * O freio anunciado que JÁ VENCEU (o beacon é de antes do prazo):
 * "o freio que a placa informou (cooloff) venceu às 14:32". null sem freio
 * anunciado ou com ele ainda ativo.
 */
export const fotaHoldExpiredText = (d: FotaHoldFields, now: Date = new Date()): string | null => {
  const until = fotaHoldUntil(d)
  if (until == null || until.getTime() > now.getTime()) return null
  const qual = d.fota_hold_reason === 'blacklist'
    ? ` (pausa da versão ${d.fota_hold_ver ?? 'travada'})`
    : d.fota_hold_reason === 'cooloff'
      ? ' (cooloff)'
      : d.fota_hold_reason === 'spacing' ? ' (espaçamento)' : ''
  const quando = until.toDateString() === now.toDateString() ? `às ${fmtHoraCurta(until)}` : `em ${fmtDiaHoraCurta(until)}`
  return `o freio que a placa informou${qual} venceu ${quando}`
}

/**
 * Extrai o motivo do 409 `device_not_suppressed` do retomar. null quando o
 * erro é outra coisa (aí é erro de verdade). Tolerante como o do canário.
 */
export function resumeConflictFromError (err: unknown): DeviceOfferResumeRefusal | null {
  const e = err as { status?: number, data?: { error?: string, reason?: unknown } } | null
  if (e?.status !== 409 || e?.data?.error !== 'device_not_suppressed') return null
  const r = e.data.reason
  return r === 'not_suppressed' || r === 'no_active_release' || r === 'storage_downgrade_blocked' ? r : null
}

export const resumeConflictLabel = (r: DeviceOfferResumeRefusal): string => {
  if (r === 'no_active_release') return 'O board desta placa está sem release ativa — não há oferta a retomar.'
  if (r === 'storage_downgrade_blocked') {
    return `Esta placa está no anel e a release ativa do board é anterior à ${RING_STORAGE_FROM}: retomar não entregaria nada — o servidor recusa essa oferta no próximo check-in, porque a imagem antiga formataria a partição. Publique uma release ${RING_STORAGE_FROM} ou maior para o board.`
  }
  return 'A oferta já não está suspensa para esta placa (a contagem expirou ou alguém retomou antes) — ela sai no próximo check-in.'
}

// ── Canário por release (lista de MACs digitada no console) ─────────────────
const MAC_RE = /^([0-9A-F]{2}:){5}[0-9A-F]{2}$/

/**
 * Normaliza um MAC digitado (qualquer caixa/separador) para AA:BB:CC:DD:EE:FF —
 * o formato canônico do servidor (mesma regra do normalizeMac do ingest).
 * null se não for um MAC.
 */
export const normalizeMacInput = (raw: string): string | null => {
  const hex = raw.trim().toUpperCase().replace(/[^0-9A-F]/g, '')
  if (hex.length !== 12) return null
  const mac = (hex.match(/.{2}/g) ?? []).join(':')
  return MAC_RE.test(mac) ? mac : null
}

/**
 * Lista de MACs digitada no campo de canário: um por linha, ou separados por
 * vírgula / ponto-e-vírgula / espaço. Devolve os válidos (canônicos, sem
 * repetição) e os tokens inválidos — o erro do formulário aponta QUAL token
 * está errado em vez de só recusar o campo.
 */
export const parseMacList = (text: string): { macs: string[], invalid: string[] } => {
  const macs: string[] = []
  const invalid: string[] = []
  for (const tok of text.split(/[\n,;\s]+/)) {
    const t = tok.trim()
    if (t === '') continue
    const mac = normalizeMacInput(t)
    if (mac == null) invalid.push(t)
    else if (!macs.includes(mac)) macs.push(mac)
  }
  return { macs, invalid }
}

/** Últimos 4 hex do MAC — o "nome curto" do sensor no produto (971C, 1EE4…). */
export const macShort = (mac: string): string =>
  mac.replace(/[^0-9A-Fa-f]/g, '').slice(-4).toUpperCase()

/** `mac` está em `list`? Indiferente a caixa/separador (a mesma regra do servidor). */
export const macInList = (list: readonly string[], mac: string): boolean => {
  const me = normalizeMacInput(mac)
  return me != null && list.some((m) => normalizeMacInput(m) === me)
}

// ── Canário como a tela o descreve ──────────────────────────────────────────
/**
 * O que a tela sabe do canário de uma release. A LISTA (`canary_macs`) só
 * chega a quem tem papel de plataforma — MAC de device é dado da empresa dona;
 * o company_admin recebe `canary_macs: null` e `canary_count`, e assim sabe
 * que a release está restrita (e a quantos devices) sem ver quem.
 */
export interface CanaryView {
  /** A release está restrita a um canário? (vale para qualquer leitor) */
  restricted: boolean
  count: number
  /** MACs canônicos, ou null para quem não pode vê-los. */
  macs: string[] | null
  /** "971C, 1EE4" quando a lista é visível; "2 devices" quando só há contagem. */
  label: string
}

export const canaryOf = (r: { canary_macs: string[] | null, canary_count: number }): CanaryView => {
  const macs = r.canary_macs != null && r.canary_macs.length > 0 ? r.canary_macs : null
  const count = macs ? macs.length : Math.max(0, Math.trunc(Number(r.canary_count) || 0))
  const label = macs ? macs.map(macShort).join(', ') : `${count} device${count === 1 ? '' : 's'}`
  return { restricted: count > 0, count, macs, label }
}

/** Concordância da frase-chave do canário: "só ele recebe" / "só eles recebem". */
export const canaryOnlyPhrase = (v: Pick<CanaryView, 'count'>): string =>
  v.count === 1 ? 'só ele recebe' : 'só eles recebem'

/** O 409 do PATCH do canário: quem não vai receber a oferta, por motivo. */
export interface CanaryConflict {
  /** Fora da frota: não cadastrados, ou com a chave inativa (issue #112). */
  unknown: string[]
  /** Release marcada: cadastrados ainda sem credenciais próprias (issue #117). */
  unmigrated: string[]
}

/**
 * Extrai o 409 `canary_unknown_macs` do PATCH do canário. Devolve `null`
 * quando o erro é outra coisa — assim o chamador distingue "precisa confirmar"
 * de "deu ruim de verdade". Tolerante de propósito: o corpo vem do servidor,
 * mas um 409 mal formado não pode virar exceção na renderização de um diálogo
 * (e o servidor anterior à #117 não manda `unmigrated_macs`).
 */
export function canaryConflictFromError (err: unknown): CanaryConflict | null {
  const e = err as { status?: number, data?: { error?: string, unknown_macs?: unknown, unmigrated_macs?: unknown } } | null
  if (e?.status !== 409 || e?.data?.error !== 'canary_unknown_macs') return null
  const macs = (v: unknown): string[] => (Array.isArray(v) ? v.filter((m): m is string => typeof m === 'string') : [])
  return { unknown: macs(e.data.unknown_macs), unmigrated: macs(e.data.unmigrated_macs) }
}

/** "2 de 3 MACs não estão na frota" — o texto do aviso, num lugar só. */
export function unknownMacsLabel (unknown: string[], total: number): string {
  const n = unknown.length
  return `${n} de ${total} ${n === 1 ? 'MAC não está' : 'MACs não estão'} na frota`
}

/** "1 de 3 MACs ainda sem credenciais próprias" — o aviso da release marcada. */
export function unmigratedMacsLabel (unmigrated: string[], total: number): string {
  const n = unmigrated.length
  return `${n} de ${total} ${n === 1 ? 'MAC' : 'MACs'} ainda sem credenciais próprias`
}

// ── Gate de credenciais próprias (release marcada, issue #117) ──────────────
/**
 * Da 3.51.0 em diante o firmware não traz segredo compilado, e o servidor
 * marca a release sozinho quando o upload não diz. Espelho do
 * CREDS_GATE_DEFAULT_FROM do servidor (bundle separado).
 */
export const CREDS_GATE_DEFAULT_FROM = '3.51.0'
/**
 * Piso que o servidor IMPÕE a release marcada (espelho do
 * CREDS_GATE_MIN_CURRENT): abaixo da 3.50.0 o firmware não anuncia creds_seq.
 */
export const CREDS_GATE_MIN_CURRENT = '3.50.0'

/**
 * A versão nasce marcada? O mesmo critério do default do servidor (≥ 3.51.0,
 * SemVer campo a campo). Fora de X.Y.Z = não: o campo ainda está sendo
 * digitado, e o servidor recusa essa versão de qualquer jeito.
 */
export const credsGateByDefault = (versao: string): boolean =>
  (cmpFwVersions(versao, CREDS_GATE_DEFAULT_FROM) ?? -1) >= 0

// ── Trava do anel (issue #126) ──────────────────────────────────────────────
/**
 * Versão que introduziu o anel de setores na partição crua — espelho do
 * RING_STORAGE_FROM do servidor (bundle separado). Release ABAIXO dela não é
 * ofertada a placa que anuncia `storage: "ring"`: essa imagem montaria a
 * partição como SPIFFS e a FORMATARIA, levando a fila e a trilha.
 */
export const RING_STORAGE_FROM = '3.54.0'

/** A placa anunciou que já migrou a fila para o anel? Só o literal do contrato. */
export const isOnRing = (storage: string | null | undefined): boolean => storage === 'ring'

/**
 * A release é anterior ao anel? Comparação SemVer campo a campo (nunca string:
 * '3.9.0' < '3.10.0'). Versão fora de X.Y.Z = não trava — o servidor recusa
 * essa versão no upload de qualquer jeito.
 */
export const isPreRingVersion = (versao: string): boolean =>
  (cmpFwVersions(versao, RING_STORAGE_FROM) ?? 0) < 0

/** Rótulo curto + o porquê (tooltip) — motivos de oferta e do gate. */
export interface ReasonMeta {
  label: string
  hint: string
}

/**
 * Motivo do decideOffer no último beacon (`last_offer_reason`). Os mesmos
 * nomes do OFFER_REASONS do servidor; o texto do contrato
 * (docs/raiz-fota-contract.md §3) em pt-BR de tela.
 */
const OFFER_REASON_META: Record<OfferReason, ReasonMeta> = {
  ok: { label: 'Ofertada', hint: 'O servidor ofertou a release ativa do board neste beacon.' },
  no_active: { label: 'Sem release ativa', hint: 'Não havia release ativa para o board deste device.' },
  not_newer: { label: 'Em dia', hint: 'O device já roda a versão da release ativa, ou uma maior (anti-downgrade).' },
  below_min: { label: 'Abaixo da versão mínima', hint: 'O firmware atual está abaixo do piso da release (versão mínima atual): o pulo é bloqueado.' },
  no_space: { label: 'Sem espaço no slot OTA', hint: 'O binário não cabe no slot OTA livre que o device informou.' },
  suppressed_excursion: { label: 'Adiada por excursão', hint: 'Release obrigatória não é entregue a device em excursão — o monitoramento vem antes.' },
  insecure_url: { label: 'URL insegura', hint: 'O manifesto da release não tem URL https — a oferta é vetada.' },
  device_failed: { label: 'Suspensa por falha repetida', hint: 'Esta placa falhou a versão repetidas vezes; o servidor parou de ofertá-la a ela.' },
  fleet_halted: { label: 'Release freada', hint: 'O freio de frota suspendeu a release (onda de rollback).' },
  not_in_canary: { label: 'Fora do canário', hint: 'A release está restrita a um canário que não inclui este device.' },
  creds_pending: {
    label: 'Aguardando migração de credenciais',
    hint: 'A release exige credenciais próprias no sensor e ele ainda não as tem agora (não confirmou, ou o último beacon veio sem elas). A oferta sai no beacon seguinte ao que confirmar.',
  },
  storage_downgrade_blocked: {
    label: 'Bloqueada: a placa está no anel',
    hint: `Esta placa já migrou a fila para o anel na partição crua e a release ativa é anterior à ${RING_STORAGE_FROM}: essa imagem formataria a partição e apagaria a fila e a trilha. A oferta volta quando o board tiver uma release ${RING_STORAGE_FROM} ou maior.`,
  },
}

/** Motivo desconhecido (servidor mais novo que o bundle) cai no nome cru — nunca exceção. */
export const offerReasonMeta = (r: OfferReason): ReasonMeta =>
  OFFER_REASON_META[r] ?? { label: r, hint: r }

/** Por que o sensor fica fora da release marcada (censo da ativação e console de credenciais). */
const CREDS_GATE_REASON_META: Record<CredsGateReason, ReasonMeta> = {
  fw_below_min: {
    label: `Firmware abaixo da ${CREDS_GATE_MIN_CURRENT}`,
    hint: `Firmware abaixo da ${CREDS_GATE_MIN_CURRENT} (ou desconhecido) não anuncia credenciais. Com uma release marcada ativa, só sobe desativando-a e reativando a ${CREDS_GATE_MIN_CURRENT}, ou pela bancada.`,
  },
  creds_not_confirmed: {
    label: 'Não confirmou credenciais',
    hint: 'O sensor nunca confirmou credenciais próprias: ainda não migrou.',
  },
  creds_not_in_nvs: {
    label: 'Sem credenciais na NVS',
    hint: 'Confirmou credenciais próprias algum dia, mas o último beacon veio sem elas (NVS apagada ou firmware revertido).',
  },
}

export const credsGateReasonMeta = (r: CredsGateReason): ReasonMeta =>
  CREDS_GATE_REASON_META[r] ?? { label: r, hint: r }

/**
 * Extrai o 409 `creds_census` da ativação de release marcada: quem ela não
 * alcança — e NADA foi ativado. `null` quando o erro é outra coisa. Tolerante
 * como o do canário: linha sem MAC é descartada, campo torto vira null.
 */
export function credsCensusFromError (err: unknown): CredsCensusDevice[] | null {
  const e = err as { status?: number, data?: { error?: string, devices?: unknown } } | null
  if (e?.status !== 409 || e?.data?.error !== 'creds_census') return null
  const list = e.data.devices
  if (!Array.isArray(list)) return []
  const str = (v: unknown): string | null => (typeof v === 'string' ? v : null)
  return list.flatMap((d): CredsCensusDevice[] => {
    const o = (d ?? {}) as Record<string, unknown>
    if (typeof o.mac !== 'string') return []
    return [{
      mac: o.mac, label: str(o.label), company_id: str(o.company_id), company_name: str(o.company_name),
      fw: str(o.fw), reason: o.reason as CredsGateReason, last_seen_at: str(o.last_seen_at),
    }]
  })
}
